import React from "react";

import { setUserToMonitor } from "@/monitoring";
import KVStore from "@/storage/kv-store";
import { config } from "@/utils/config";
import { unregisterSw } from "@/utils/sw";

import { HStack } from "./ui";

function goHome() {
  location.href = "/";
}

interface ErrorScreenProps {
  errorCode?: string;
}

export default function ErrorScreen(props: ErrorScreenProps) {
  const { errorCode = "ERR_NETWORK" } = props;

  function clearCookies(event: React.MouseEvent) {
    localStorage.clear();
    sessionStorage.clear();
    KVStore.clear();
    setUserToMonitor(null);

    if ("serviceWorker" in navigator) {
      event.preventDefault();
      unregisterSw().then(goHome).catch(goHome);
    }
  }

  function renderTitle() {
    switch (errorCode) {
      case "ERR_NETWORK":
        return "API Connection Error";
      default:
        return "Something went wrong.";
    }
  }

  function renderBody() {
    switch (errorCode) {
      case "ERR_NETWORK":
        return "Our API service is currently unavailable. This could be due to maintenance, high traffic, or a temporary outage. We're working to resolve this as quickly as possible.";
      default:
        return "We're sorry for the interruption.";
    }
  }

  return (
    <div className="flex h-screen flex-col bg-white pt-16 pb-12 dark:bg-gray-900">
      <main className="mx-auto flex w-full max-w-7xl grow flex-col justify-center px-4 sm:px-6 lg:px-8">
        <div className="py-8">
          <div className="mx-auto max-w-xl space-y-2 text-center">
            <h1 className="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl dark:text-gray-500">
              {renderTitle()}
            </h1>

            <p className="text-lg text-muted-foreground">
              {renderBody()} If the problem persists, please reach out to our
              support team. You may also try to{" "}
              <a
                href="/"
                onClick={clearCookies}
                className="text-primary-600 hover:underline dark:text-primary-400"
              >
                clear cookies and browser data
              </a>{" "}
              (this will log you out).
            </p>
          </div>
        </div>
      </main>

      <footer className="mx-auto w-full max-w-7xl shrink-0 px-4 sm:px-6 lg:px-8">
        <HStack justifyContent="center" space={4} element="nav">
          <a
            href={config.links.status.href}
            className="text-sm font-medium text-muted-foreground hover:underline"
          >
            {config.links.status.fallbackMessage}
          </a>

          <>
            <span className="inline-block border-l" aria-hidden="true" />
            <a
              href={config.links.help.href}
              className="text-sm font-medium text-muted-foreground hover:underline"
            >
              {config.links.help.fallbackMessage}
            </a>
          </>

          <>
            <span className="inline-block border-l" aria-hidden="true" />
            <a
              href={config.links.support.href}
              className="text-sm font-medium text-muted-foreground hover:underline"
            >
              {config.links.support.fallbackMessage}
            </a>
          </>
        </HStack>
      </footer>
    </div>
  );
}
